import { Button, BUTTON_TYPES } from '../../../components/Button'
import { BTN_TXT, PLACEHOLDERS } from '../../../constants'
import { Popup } from '../../../components/Popup'
import { Typography, TYPOGRAPHY_WEIGHT } from '../../../components/Typography'
import './styles.scss'
import { useCallback, useEffect } from 'react'
import { UserContractCategory, TCCRelatedItemReassign } from '../types'
import { LeadByIndicator } from '../../../components/LeadByIndicator'
import {
  LED_BY_LABEL,
  LED_BY_VALUE
} from '../../../components/LeadByIndicator/constants'
import { Select } from '../../../components/Select'
import { Separator } from '../../../components/Separator/Separator'
import { useCRUDUserContext } from '../../../pages/CRUDUser/Provider/CRUDUserProvider'
import { Datepicker } from '../../../components/Datepicker'

import { useCRUDUserPopupProviderContext } from '../../../pages/CRUDUser/Provider/CRUDUserPopupProvider'
import moment from 'moment'
import { CustomForm } from 'components/Forms/CustomForm'
import { useSelector } from 'react-redux'
import { getUser } from '../../../redux/store/user/getters'
import { ROLES } from '../../Permission'
import { formatReassignDataToForm, formatReassignDataToRequest } from './helper'

type ModalProps = {
  selectedCC: UserContractCategory | null
  userName?: string
}

export const ManageAssignmentWithCoOrRFPHSPresidentPopup = ({
  selectedCC,
  userName
}: ModalProps) => {
  const {
    state: {
      form,
      CCRelatedItems: { contracts, rfp },
      ccOwnerOptions,
      reassignmentRequest,
      isReassign
    },
    actions: { setSelectedCC, reassignCCResponsible, updateReassignmentRequest }
  } = useCRUDUserContext()

  const {
    assignWithRelatedItemsHSPresPopup: {
      actions: { close },
      state: { visible, title }
    }
  } = useCRUDUserPopupProviderContext()

  const user = useSelector(getUser)

  const onCancel = useCallback(() => {
    close()
    setSelectedCC(null)
    form.resetFields()
  }, [close, setSelectedCC, form])

  const onSubmit = useCallback(
    async (values: TCCRelatedItemReassign) => {
      if (selectedCC?.uuid) {
        const data = formatReassignDataToRequest(values, isReassign)
        if (reassignmentRequest) {
          await updateReassignmentRequest(selectedCC.uuid, data)
        } else {
          await reassignCCResponsible(selectedCC.uuid, data)
        }
      }
    },
    [
      isReassign,
      selectedCC?.uuid,
      reassignmentRequest,
      updateReassignmentRequest,
      reassignCCResponsible
    ]
  )

  const onChangeResponsible = useCallback(
    (led_by: string, value: string, isRfp: boolean) => {
      if (led_by === LED_BY_VALUE.COMMUNITY) {
        form.setFieldsValue({
          [`${led_by}_responsible_${isRfp ? 'contract' : 'rfp'}`]: value
        })
      }
    },
    [form]
  )

  useEffect(() => {
    if (reassignmentRequest && visible) {
      const data = formatReassignDataToForm(reassignmentRequest)
      form.setFieldsValue(data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, reassignmentRequest])

  if (!selectedCC) return null

  return (
    <Popup
      visible={visible}
      title={title}
      onCancel={onCancel}
      width={676}
      className="add-contract-category-popup"
      footer={
        <>
          <Button upperCase onClick={onCancel}>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            form="reassign-with-connected-items-form"
            htmlType="submit"
            upperCase
          >
            {reassignmentRequest ? BTN_TXT.SAVE : BTN_TXT.REASSIGN}
          </Button>
        </>
      }
    >
      <CustomForm<TCCRelatedItemReassign>
        form={form}
        onFinish={onSubmit}
        id="reassign-with-connected-items-form"
        className="column gap-24 hs-reassign-popup-with-rfp-or-contracts"
      >
        <Typography.Body2 className="hs-reassign-popup-with-rfp-or-contracts__grey-text">
          You are about to reassign {selectedCC.name} contract category from{' '}
          {userName} profile. Reassignment to another responsible is required
          for all related items.
        </Typography.Body2>
        {!!contracts.length && (
          <div className="column gap-16">
            <div>
              <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                Contracts
              </Typography.Body1>
              <Typography.Body2 className="hs-reassign-popup-with-rfp-or-contracts__grey-text">
                Select a responsible user to reassign all contracts.
              </Typography.Body2>
            </div>
            <div className="column gap-8">
              {contracts.map(({ led_by, can_be_edited }) => (
                <div
                  key={led_by}
                  className="hs-reassign-popup-with-rfp-or-contracts__reassign-row row space-between"
                >
                  <div className="row gap-8 align-center">
                    <LeadByIndicator value={led_by} />
                    {LED_BY_LABEL[led_by]} contracts
                  </div>
                  <Select
                    className="hs-reassign-popup-with-rfp-or-contracts__reassign-row__select"
                    propsItem={{
                      name: `${led_by}_responsible_contract`,
                      rules: [
                        {
                          required: true,
                          message: 'Contract steward is required'
                        }
                      ]
                    }}
                    propsSelect={{
                      disabled: !can_be_edited,
                      getPopupContainer: (node) => document?.body ?? node,
                      placeholder: PLACEHOLDERS.PLEASE_SELECT,
                      onChange: (value) =>
                        onChangeResponsible(led_by, value, false)
                    }}
                    options={ccOwnerOptions}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {!!rfp.length && (
          <>
            {!!contracts.length && <Separator />}
            <div className="column gap-16">
              <div>
                <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                  RFP
                </Typography.Body1>
                <Typography.Body2 className="hs-reassign-popup-with-rfp-or-contracts__grey-text">
                  Reassigning the RFP will transfer all associated categories to
                  the new user.
                </Typography.Body2>
              </div>
              <div className="column gap-8">
                {rfp.map(({ led_by, can_be_edited }) => (
                  <div
                    key={led_by}
                    className="hs-reassign-popup-with-rfp-or-contracts__reassign-row row space-between"
                  >
                    <div className="row gap-8 align-center">
                      <LeadByIndicator value={led_by} />
                      {LED_BY_LABEL[led_by]} RFP
                    </div>
                    <Select
                      className="hs-reassign-popup-with-rfp-or-contracts__reassign-row__select"
                      propsItem={{
                        name: `${led_by}_responsible_rfp`,
                        rules: [
                          {
                            required: true,
                            message: `${
                              user.role === ROLES.COMMONS_VICE_PRESIDENT
                                ? 'OM Lifeline c'
                                : 'C'
                            }ontract steward is required`
                          }
                        ]
                      }}
                      propsSelect={{
                        disabled: !can_be_edited,
                        getPopupContainer: (node) => document?.body ?? node,
                        placeholder: PLACEHOLDERS.PLEASE_SELECT,
                        onChange: (value) =>
                          onChangeResponsible(led_by, value, true)
                      }}
                      options={ccOwnerOptions}
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {(user.role === ROLES.VICE_PRESIDENT ||
          user.role === ROLES.COMMONS_VICE_PRESIDENT ||
          (user.role === ROLES.COMMUNITY_PRESIDENT && reassignmentRequest)) && (
          <>
            <Separator />
            <div className="column gap-16">
              <div>
                <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                  Effective date
                </Typography.Body1>
                <Typography.Body2 className="hs-reassign-popup-with-rfp-or-contracts__grey-text">
                  Select date of reassignment
                </Typography.Body2>
              </div>
              <Datepicker
                propsItem={{
                  name: 'effective_date',
                  rules: [
                    {
                      required: true,
                      message: 'Effective date is required'
                    }
                  ]
                }}
                propsDate={{
                  disabled: user.role === ROLES.COMMUNITY_PRESIDENT,
                  placeholder: PLACEHOLDERS.SELECT_DATE,
                  disabledDate: (current) =>
                    current.isBefore(moment().startOf('day'))
                }}
                className="hs-reassign-popup-with-rfp-or-contracts__effective-date-picker"
              />
            </div>
          </>
        )}
      </CustomForm>
    </Popup>
  )
}
